import React, { useCallback, useContext, useState } from 'react';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';
import { Experience, ExperienceStatus } from '@/webapi/use-experience-api';
import { EditorContext } from '@/features/editor/context/editor-context';
import { useCanPublish } from '@/features/accounts/permissions';
import { centered, useSharedElement } from '@/components/use-shared-element';
import { ConfirmModal } from '@/components/confirm-modal';
import { SharedElementOverlay } from '@/components/shared-element-overlay';
import { Checkbox } from '@/components/checkbox';
import { EDITOR_EXPERIENCE_CACHE } from '@/components/hooks/use-cached-auto-save';
import { UseExperienceControls } from '@/features/editor/context/use-experience-controls';
import { VSpace } from '@/components/spacing';
import { parseScheduledTime } from '@/utils/experience-utils';

function hasAllocChanged(currentExperience: Experience) {
  return !!currentExperience.variants.find(
    ({ publishedChance, chance }) =>
      typeof publishedChance !== `undefined` && publishedChance !== chance,
  );
}

export function useScheduleModal(
  originRef: React.MutableRefObject<HTMLElement>,
  _currentExperience?: Experience,
  _experienceControls?: UseExperienceControls,
) {
  const edCtx = useContext(EditorContext);
  const experienceControls = _experienceControls || edCtx?.experienceControls;
  const currentExperience =
    _currentExperience || edCtx?.experienceState?.currentExperience;
  const isThemeTest = currentExperience?.isThemeTest;

  const canPublish = useCanPublish();

  const [shouldResetData, setShouldResetData] = useState(true);
  const isReScheduled =
    currentExperience?.status === ExperienceStatus.PUBLISHED &&
    currentExperience?.isScheduleActive;

  let heightRem = isReScheduled ? 19 : 17;
  if (isThemeTest) {
    heightRem = 23;
  }
  const {
    props: scheduleConfirmProps,
    show,
    hide,
  } = useSharedElement(
    {
      showBackdrop: true,
      extraFrom: {
        background: `#000000`,
        border: `2px solid #000000`,
      },
      extraTo: {
        background: `#FFFFFF`,
        border: `2px solid #FFFFFF`,
      },
    },
    originRef,
    () => centered(heightRem, 42),
  );

  let title = isReScheduled
    ? `Rescheduled Experience?`
    : `Schedule Experience?`;
  const defaultDescription = `Are you sure you want to schedule this experience? Once confirm it will start ${parseScheduledTime(
    currentExperience?.schedule,
  )}`;
  let description: any = defaultDescription;
  let yesCaption = `Yes, Schedule`;
  let noCaption = `No`;

  if (isReScheduled) {
    description = (
      <ModalWrapper>
        <Checkbox
          initialValue={shouldResetData}
          onSelection={setShouldResetData}
        />
        <div className="header">
          Clear past collected data?{` `}
          <div className="body">
            Reset past collected data in order to start a fresh A/B experiment.
          </div>
        </div>
      </ModalWrapper>
    );
    yesCaption = `Yes, Reschedule`;
    noCaption = `No`;
  }

  if (hasAllocChanged(currentExperience)) {
    title = `Are you sure you want to change the allocation?`;
    description = (
      <div>
        <VSpace value={2} />
        <p>A new test version will be made</p>
      </div>
    );
    yesCaption = `Yes`;
  }

  if (!canPublish) {
    title = `Schedule?`;
    description = `Your user has insufficient privileges to schedule an experience`;
    yesCaption = `Dismiss`;
  }
  if (isThemeTest) {
    description = (
      <>
        <span>
          Once you stop the experience, a process to unassign visitors from a
          specific theme will begin and be completed within two days.{` `}
          <strong>
            During this time, avoid removing the Visually script from the tested
            themes.
          </strong>
        </span>
        <VSpace />
        <span>{defaultDescription}</span>
      </>
    );
  }

  const onDiscard = async () => {
    hide();
  };

  const onPublish = useCallback(async () => {
    if (canPublish) {
      EDITOR_EXPERIENCE_CACHE.disableWrites();
      await experienceControls.publish(shouldResetData);
      schedulePublishSuccessToast();
      hide();
      experienceControls.closeAndNavigateToDashboard();
      EDITOR_EXPERIENCE_CACHE.enableWrites();
    }
  }, [currentExperience, shouldResetData]);

  return {
    scheduleConfirmProps,
    onDiscard,
    onPublish,
    show,
    hide,
    canPublish,
    yesCaption,
    noCaption,
    title,
    description,
  };
}

export function ScheduleModal({
  scheduleConfirmProps,
  canPublish,
  yesCaption,
  noCaption,
  description,
  hide,
  title,
  onDiscard,
  onPublish,
}) {
  return (
    <SharedElementOverlay {...scheduleConfirmProps} heightMultiplier={1.2}>
      <ConfirmModal
        neutral
        onlyDismiss={!canPublish}
        yesCaption={yesCaption}
        noCaption={noCaption}
        title={title}
        description={description}
        onDiscard={onDiscard}
        onClose={hide}
        onConfirm={onPublish}
        btnSize="medium"
      />
    </SharedElementOverlay>
  );
}

function schedulePublishSuccessToast() {
  setTimeout(
    () =>
      toast(`Scheduled Successfully`, {
        theme: `colored`,
        type: `success`,
        className: css({
          fontFamily: `JetBrains Mono, Serif`,
          fontWight: `700`,
        }),
      }),
    1000,
  );
}

const ModalWrapper = styled.div`
  display: grid;
  grid-template-columns: 3rem 1fr;
  text-align: left;
  grid-gap: 1rem;

  .header {
    color: #8c8c8c;
    font-weight: bold;
    line-height: 1.5;
  }

  .body {
    font-weight: 500;
  }
`;
